/**
 * WOM Collection Ratings (TrueBrand)
 * Shows WOM star rating + review count on collection/product listing cards.
 * Handles dynamically loaded content (related products, quick view, etc.)
 */
(function() {
  'use strict';

  const STATE_KEY = '__womCollectionRatings_energyfitness';
  const API_URL = 'https://api.thewordofmouth.in/api/overall_company/energyfitness';

  // Prevent multiple initializations
  if (window[STATE_KEY] && window[STATE_KEY].initialized) {
    return;
  }

  window[STATE_KEY] = window[STATE_KEY] || { data: null, fetching: false, initialized: false };
  const state = window[STATE_KEY];
  state.initialized = true;

  // Define global gradient for stars (orange to yellow)
  function ensureGradient() {
    if (document.getElementById('wom-star-gradient')) return;
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('style', 'position: absolute; width: 0; height: 0;');
    const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
    const gradient = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient');
    gradient.setAttribute('id', 'wom-star-gradient');
    gradient.setAttribute('x1', '0%');
    gradient.setAttribute('y1', '0%');
    gradient.setAttribute('x2', '100%');
    gradient.setAttribute('y2', '100%');
    const stop1 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
    stop1.setAttribute('offset', '0%');
    stop1.setAttribute('style', 'stop-color:#FFA500;stop-opacity:1');
    const stop2 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
    stop2.setAttribute('offset', '100%');
    stop2.setAttribute('style', 'stop-color:#FFD700;stop-opacity:1');
    gradient.appendChild(stop1);
    gradient.appendChild(stop2);
    defs.appendChild(gradient);
    svg.appendChild(defs);
    document.body.appendChild(svg);
  }

  function slugifyTitle(title) {
    if (!title) return '';
    let filtered = title.split(/\s*[-–]\s*/)[0].trim();
    filtered = filtered
      .replace(/[^\w]+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_+|_+$/g, '')
      .toLowerCase();
    return filtered;
  }

  function resolveData(element, dataset) {
    if (!dataset) return null;
    const rawHandle = (element.getAttribute('data-product-handle') || '').toLowerCase();
    const rawTitle = element.getAttribute('data-product-title') || '';
    const slugFromTitle = slugifyTitle(rawTitle);

    // Try product handle first
    if (rawHandle && dataset[rawHandle]) {
      return dataset[rawHandle];
    }

    // Try handle variants
    const handleVariants = [
      rawHandle,
      rawHandle.replace(/-/g, '_'),
      rawHandle.replace(/_/g, '-'),
    ].filter(Boolean);

    for (const variant of handleVariants) {
      if (dataset && dataset[variant]) {
        return dataset[variant];
      }
    }

    // Fallback: Try exact title match
    if (dataset[rawTitle]) {
      return dataset[rawTitle];
    }

    // Try title slug variants as last resort
    const titleVariants = [
      slugFromTitle,
      slugFromTitle.replace(/_/g, '-'),
    ].filter(Boolean);

    for (const variant of titleVariants) {
      if (dataset && dataset[variant]) {
        return dataset[variant];
      }
    }

    return null;
  }

  function getStarColor() {
    try {
      const config = window.vesselsWOMConfig || window.clientWOMConfig || {};
      const colors = config.colors || {};
      return colors.starColor || colors.accent || colors.primary || '#ffa503';
    } catch (error) {
      return '#ffa503';
    }
  }

  function generateStars(rating) {
    ensureGradient();
    const normalizedRating = Math.min(Math.max(rating, 0), 5); // Clamp between 0 and 5
    let starsHTML = '';
    const starPath = 'M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z';
    
    // Generate 5 stars, all filled proportionally based on rating
    for (let i = 0; i < 5; i++) {
      const starId = `wom-star-${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${i}`;
      const starValue = normalizedRating - i; // Value for this star (0-1)
      const fillPercent = Math.min(Math.max(starValue * 100, 0), 100);
      
      if (fillPercent >= 100) {
        // Fully filled star
        starsHTML += `<svg viewBox="0 0 20 20" style="overflow: visible;">
          <defs>
            <linearGradient id="${starId}-grad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" style="stop-color:#FFA500;stop-opacity:1" />
              <stop offset="100%" style="stop-color:#FFD700;stop-opacity:1" />
            </linearGradient>
          </defs>
          <path fill="url(#${starId}-grad)" stroke="#FFA500" stroke-width="0.5" d="${starPath}" />
        </svg>`;
      } else if (fillPercent > 0) {
        // Partial star - filled portion with gradient, unfilled portion transparent with colored border
        starsHTML += `<svg viewBox="0 0 20 20" style="overflow: visible;">
          <defs>
            <linearGradient id="${starId}-grad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" style="stop-color:#FFA500;stop-opacity:1" />
              <stop offset="100%" style="stop-color:#FFD700;stop-opacity:1" />
            </linearGradient>
            <clipPath id="${starId}-clip">
              <rect x="0" y="0" width="${fillPercent}%" height="100%" />
            </clipPath>
          </defs>
          <!-- Star outline with colored border (unfilled portion visible) -->
          <path fill="transparent" stroke="#FFA500" stroke-width="1" d="${starPath}" />
          <!-- Filled portion clipped to percentage -->
          <path fill="url(#${starId}-grad)" stroke="none" clip-path="url(#${starId}-clip)" d="${starPath}" />
        </svg>`;
      } else {
        // Empty star - transparent fill with colored border
        starsHTML += `<svg viewBox="0 0 20 20"><path fill="transparent" stroke="#FFA500" stroke-width="1" d="${starPath}" /></svg>`;
      }
    }
    
    return starsHTML;
  }

  function updateElements() {
    if (!state.data) return;
    const elements = document.querySelectorAll('.wom-collection-rating');
    let foundCount = 0;
    
    elements.forEach((element) => {
      // Skip already processed elements
      if (element.getAttribute('data-wom-processed') === 'true') {
        if (element.style.display === 'flex') foundCount++;
        return;
      }

      const data = resolveData(element, state.data);
      if (data && Number(data.overall_rating) > 0 && Number(data.total_reviews) > 0) {
        const ratingEl = element.querySelector('.wom-rating-number');
        const starsEl = element.querySelector('.wom-rating-stars');
        const countEl = element.querySelector('.wom-rating-count');
        
        if (ratingEl) ratingEl.textContent = Number(data.overall_rating).toFixed(1);
        if (starsEl) starsEl.innerHTML = generateStars(Number(data.overall_rating));
        if (countEl) countEl.textContent = `(${data.total_reviews} Reviews)`;
        element.style.display = 'flex';
        element.setAttribute('data-wom-processed', 'true');
        foundCount++;
      } else {
        element.style.display = 'none';
        element.setAttribute('data-wom-processed', 'true');
      }
    });
    
    if (foundCount > 0) {
      console.log('WOM Collection Ratings: Updated', foundCount, 'of', elements.length, 'products');
    }
  }

  function fetchData(force) {
    if (state.fetching) return;
    if (!force && state.data) {
      updateElements();
      return;
    }

    state.fetching = true;
    fetch(API_URL, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    })
      .then((response) => response.json())
      .then((data) => {
        state.data = data || {};
        state.fetching = false;
        console.log('WOM Collection Ratings: Data fetched', Object.keys(state.data).length, 'products');
        updateElements();
      })
      .catch((error) => {
        console.error('WOM Collection Ratings: Failed to fetch', error);
        state.fetching = false;
      });
  }

  // Ensure gradient is available
  ensureGradient();

  // Initial fetch
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      ensureGradient();
      fetchData(false);
    });
  } else {
    fetchData(false);
  }

  // Listen for custom events
  document.addEventListener('collection-updated', () => {
    state.data = null;
    fetchData(true);
  });

  document.addEventListener('wom-collection-refresh', updateElements);

  // MutationObserver for dynamically loaded products
  const observer = new MutationObserver((mutations) => {
    let hasNewRatings = false;
    
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType === 1) {
          if (node.classList && node.classList.contains('wom-collection-rating')) {
            hasNewRatings = true;
            break;
          }
          if (node.querySelectorAll) {
            const ratings = node.querySelectorAll('.wom-collection-rating');
            if (ratings.length > 0) {
              hasNewRatings = true;
              break;
            }
          }
        }
      }
      if (hasNewRatings) break;
    }
    
    if (hasNewRatings) {
      setTimeout(() => {
        if (state.data) {
          updateElements();
        } else {
          fetchData(false);
        }
      }, 50);
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });

  // Fallback: Check for unprocessed elements every 2 seconds
  setInterval(() => {
    const unprocessed = document.querySelectorAll('.wom-collection-rating:not([data-wom-processed])');
    if (unprocessed.length > 0 && state.data) {
      updateElements();
    }
  }, 2000);

  // Expose global function for manual refresh
  window.womRefreshRatings = function() {
    document.querySelectorAll('.wom-collection-rating').forEach(el => {
      el.removeAttribute('data-wom-processed');
    });
    updateElements();
  };

  console.log('WOM Collection Ratings: Initialized');
})();
